import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { FaFileAlt, FaTimes } from 'react-icons/fa'
import { LuSparkles } from 'react-icons/lu'
import { Alert, Button, Heading, Stack, Text, Box, FormControl, Textarea, Icon, Spinner } from '@/components/lib'
import FileChooser from '../../includes/FileChooser'
import api from '../../../api'
import utils from '@/utils/utils'
import useStore from '../../../store'
import useJob from '../../../hooks/useJob'
import scorchHelpful from '../../../images/scorch/helpful.png'
import BuildReport from './BuildReport'

// Mirrors the API's limits (util/trial_builder.py): the file cap is what one
// Bedrock document block can hold, so a bigger file can't be read at all.
const MAX_FILES = 15
const MAX_FILE_MB = 4.5
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.html,.htm'

// Scorch builds the trial from a description and the study's documents. The
// build runs as a job on the API and narrates itself, so once it starts the
// form gives way to what Scorch is doing; when it finishes, a report of what
// was built and what was left for the team comes before the trial itself.
export default function TrialBuilder ({ onCreated, goToTrial }) {
  const [prompt, setPrompt] = useState('')
  const [files, setFiles] = useState([])
  const [error, setError] = useState()
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState()
  const [result, setResult] = useState()
  const navigate = useNavigate()
  const { user, team } = useStore()
  const { startPolling } = useJob()

  function addFile (file) {
    if (file.size > MAX_FILE_MB * 1000 * 1000) {
      utils.toast('error', `${file.name} is too large`, `Scorch can read documents up to ${MAX_FILE_MB} MB each.`)
      return
    }
    setFiles(prev => {
      // A multi-file pick can overshoot the cap after the files have already
      // uploaded, so say which ones were left out rather than dropping them
      // silently.
      if (prev.length >= MAX_FILES) {
        utils.toast('error', `${file.name} was not added`, `Scorch can read up to ${MAX_FILES} documents in one build.`)
        return prev
      }
      return [...prev, { name: file.name, storedName: file.storedName }]
    })
  }

  function build () {
    setError()
    setLoading(true)
    setProgress('Getting started…')
    api.teams.buildTrial(user.team, { prompt, files }, ({ jobId }) => {
      startPolling(jobId, {
        onProgress: setProgress,
        onDone: built => {
          onCreated(built.trial, { stay: true })
          setProgress('Done')
          setResult(built)
        },
        onError: message => {
          setError(message)
          setLoading(false)
        }
      })
    }, err => {
      setError(err.message)
      setLoading(false)
    })
  }

  if (loading) {
    return (
      <Stack alignItems='center' textAlign='center' gap={4} py={10}>
        <BuildReport
          isOpen={!!result}
          report={result?.report}
          warnings={result?.warnings}
          onClose={() => goToTrial(result.trial)}
        />
        <Box as='img' src={scorchHelpful} alt='Scorch' h='96px' />
        <Heading as='h3' size='md'>Scorch is building your trial</Heading>
        <Stack direction='row' alignItems='center' gap={3}>
          <Spinner color='primary.500' size='sm' />
          <Text color='fg.muted'>{progress}</Text>
        </Stack>
        <Text fontSize='sm' color='fg.muted'>This can take a few minutes when there are several documents to read. You can stay on this page.</Text>
      </Stack>
    )
  }

  return (
    <Stack gap={5}>
      <Stack direction='row' alignItems='center' gap={4}>
        <Box as='img' src={scorchHelpful} alt='Scorch' h='72px' style={{ objectFit: 'cover' }} />
        <Box>
          <Heading as='h3' size='md'>Let Scorch build your trial</Heading>
          <Text color='fg.muted'>Scorch will build your trial based on the information and files you provide it. You can upload documents such as your protocol, participant information sheet, informed consent form, and survey or questionnaire templates.</Text>
        </Box>
      </Stack>

      <FormControl label='Describe your trial' helperText='Anything Scorch should know that the documents don’t say: the study’s name, what it is testing, which forms you need, whether participants give consent.'>
        <Textarea
          autoFocus
          rows={5}
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder='A 12-week study of a sleep-support app in adults with insomnia. Participants complete a baseline questionnaire, a daily sleep diary and the PHQ-9 at weeks 0, 6 and 12…'
        />
      </FormControl>

      <FormControl label='Documents' helperText={`Up to ${MAX_FILES} documents of ${MAX_FILE_MB} MB each.`}>
        <Stack gap={2}>
          {files.length > 0 &&
            <Stack direction='row' flexWrap='wrap' gap={2}>
              {files.map((f, i) => (
                <Box key={f.storedName} display='flex' alignItems='center' gap={2} px={2} py={1} bg='bg.subtle' rounded='md'>
                  <Icon as={FaFileAlt} />
                  <Text fontSize='sm'>{f.name}</Text>
                  <Icon as={FaTimes} cursor='pointer' onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} />
                </Box>
              ))}
            </Stack>}
          {files.length < MAX_FILES &&
            <Box>
              <FileChooser
                // Uploaded against the team: the trial doesn't exist yet, and the
                // API reads them back from the team's own prefix.
                forType='team'
                forObject={team}
                accept={ACCEPT}
                multiple
                onComplete={addFile}
                onError={err => utils.toast('error', `Upload failed: ${err}`)}
                trigger={<Button size='sm' variant='outline'>Add documents</Button>}
              />
            </Box>}
        </Stack>
      </FormControl>

      <Alert variant='left-accent' status='info'>Scorch sets up the trial's details, its consent form where the study takes consent, and a stage for each form it finds. Everything it builds can be changed afterwards, and anything it couldn't work out is left for you with a note.</Alert>

      {error &&
        <Alert variant='left-accent' status='error'>Scorch couldn't build your trial: {error}</Alert>}

      <Stack direction='row' justifyContent='end' alignItems='center'>
        <Button variant='ghost' onClick={() => navigate('/trials')}>Cancel</Button>
        <Button colorPalette='primary' onClick={build} disabled={!prompt.trim() && !files.length}>
          <Icon as={LuSparkles} /> Build my trial
        </Button>
      </Stack>
    </Stack>
  )
}
